import React, { useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Print as PrintIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import api from "../../services/api";
import SquarePayment from "./SquarePayment";
import PayPayPayment from "./PayPayPayment";
import GiftCardPayment from "./GiftCardPayment";
import MemberPointPayment from "./MemberPointPayment";
import ConveniencePayment from "./ConveniencePayment";
import SplitPayment from "./SplitPayment";
import CreditPayment from "./CreditPayment";
import DigitalReceipt from "./DigitalReceipt";
import ManualPaymentDialog from "./ManualPaymentDialog";
import SquareTerminalPayment from "./SquareTerminalPayment";
import starWebPRNTService from "../../services/starWebPRNTService";

// AUDIT-11-1 FIX: 安全な金額変換関数（NaN防止、整数化）
const safeParseAmount = (value) => {
  if (value === '' || value === null || value === undefined) return 0;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.round(parsed); // 整数に丸める（円単位）
};

// AUDIT-11-7 FIX: フロントエンド税計算検証関数
// eslint-disable-next-line no-unused-vars
const _TAX_RATE_STANDARD = 0.10; // 標準税率10%
// eslint-disable-next-line no-unused-vars
const _TAX_RATE_REDUCED = 0.08;  // 軽減税率8%

const validateOrderTax = (order) => {
  if (!order || !order.items || order.items.length === 0) return { valid: true };

  // 商品合計を再計算
  let calculatedSubtotal = 0;
  order.items.forEach(item => {
    const price = item.price || item.unit_price || 0;
    const quantity = item.quantity || 1;
    calculatedSubtotal += price * quantity;
  });

  // 税込価格からの逆算（税込価格 ÷ 1.1）
  // または小計 + 税額 = 合計の検証
  const providedSubtotal = order.subtotal || 0;
  const providedTax = order.tax || 0;
  const providedTotal = order.total || 0;

  // 小計 + 税 = 合計 の検証
  const calculatedTotal = providedSubtotal + providedTax;
  const totalDifference = Math.abs(calculatedTotal - providedTotal);

  // 商品合計と小計の差異チェック
  const subtotalDifference = Math.abs(calculatedSubtotal - providedSubtotal);

  // 100円以上の差異は警告
  if (totalDifference > 100 || subtotalDifference > 100) {
    console.warn('[CHECKOUT] AUDIT-11-7: 税額計算の差異を検出', {
      calculatedSubtotal,
      providedSubtotal,
      providedTax,
      providedTotal,
      calculatedTotal,
      totalDifference,
      subtotalDifference,
    });
    return {
      valid: false,
      warning: '税額計算に差異があります。金額を確認してください。',
      details: { totalDifference, subtotalDifference },
    };
  }

  return { valid: true };
};

// キッチン印刷ヘルパー（非同期・失敗しても決済は続行）
// 自動印刷トグルチェック + カテゴリ別プリンタ振り分け対応
const triggerKitchenPrint = (order) => {
  if (!order?.items) return;
  if (!starWebPRNTService.isAutoKitchenPrintEnabled()) return;
  starWebPRNTService.printKitchenTicketWithRouting({
    tableNumber: order.tableNumber,
    items: order.items.map((item) => ({
      name: item.product?.name || item.name || '商品',
      quantity: item.quantity || 1,
      note: item.note,
      categoryId: item.product?.categoryId || item.product?.category_id || item.categoryId || item.category_id,
    })),
  }).catch((err) => console.warn('[Kitchen] 印刷スキップ:', err.message));
};

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { storeId } = useParams();
  const { order } = location.state || { order: null };
  const [receivedAmount, setReceivedAmount] = useState("");
  const [printDialog, setPrintDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [showSquarePayment, setShowSquarePayment] = useState(false);
  const [showPayPayPayment, setShowPayPayPayment] = useState(false);
  const [showGiftCardPayment, setShowGiftCardPayment] = useState(false);
  const [showMemberPointPayment, setShowMemberPointPayment] = useState(false);
  const [showConveniencePayment, setShowConveniencePayment] = useState(false);
  const [showSplitPayment, setShowSplitPayment] = useState(false);
  const [showCreditPayment, setShowCreditPayment] = useState(false);
  const [showDigitalReceipt, setShowDigitalReceipt] = useState(false);
  const [showManualPaymentDialog, setShowManualPaymentDialog] = useState(false);
  const [showSquareTerminal, setShowSquareTerminal] = useState(false);
  const [, setPaymentResult] = useState(null);
  const [completedSaleId, setCompletedSaleId] = useState(null);
  const [enabledPaymentMethods, setEnabledPaymentMethods] = useState(null);
  const [paymentError, setPaymentError] = useState(null); // エラー状態管理
  const [taxWarning, setTaxWarning] = useState(null); // AUDIT-11-7 FIX: 税額警告

  // AUDIT-11-7 FIX: 注文の税額を検証
  React.useEffect(() => {
    if (order) {
      const validation = validateOrderTax(order);
      if (!validation.valid) {
        setTaxWarning(validation.warning);
      }
    }
  }, [order]);

  // キーボードショートカット
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      // orderがない場合はスキップ
      if (!order) return;
      // ダイアログや入力フォームがアクティブの場合はスキップ
      if (printDialog || showSquarePayment || showPayPayPayment ||
          showGiftCardPayment || showMemberPointPayment ||
          showConveniencePayment || showSplitPayment || showCreditPayment) {
        return;
      }
      // テキストフィールドにフォーカスがある場合はスキップ
      const activeEl = document.activeElement;
      if (activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA')) {
        return;
      }

      switch (e.key) {
        case 'F1':
          e.preventDefault();
          setSelectedPaymentMethod(allQuickMethods.find(m => m.id === 'cash'));
          break;
        case 'F2':
          e.preventDefault();
          setSelectedPaymentMethod(allQuickMethods.find(m => m.id === 'credit_card_square'));
          break;
        case 'F3':
          e.preventDefault();
          setSelectedPaymentMethod(allQuickMethods.find(m => m.id === 'paypay'));
          break;
        case 'Enter':
          // Enterで決済確定（決済方法選択済みで、現金の場合は金額入力済み）
          if (selectedPaymentMethod && canPay()) {
            e.preventDefault();
            if (isSquarePayment) {
              setShowSquarePayment(true);
            } else if (isPayPayPayment) {
              setShowPayPayPayment(true);
            } else if (isGiftCardPayment) {
              setShowGiftCardPayment(true);
            } else if (isMemberPointPayment) {
              setShowMemberPointPayment(true);
            } else if (isConveniencePayment) {
              setShowConveniencePayment(true);
            } else if (isSplitPayment) {
              setShowSplitPayment(true);
            } else if (isCreditAccountPayment) {
              setShowCreditPayment(true);
            } else {
              handlePayment();
            }
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printDialog, showSquarePayment, showPayPayPayment, showGiftCardPayment,
      showMemberPointPayment, showConveniencePayment, showSplitPayment, showCreditPayment,
      selectedPaymentMethod, receivedAmount, order]);

  // 店舗の決済設定を取得
  React.useEffect(() => {
    const fetchPaymentSettings = async () => {
      try {
        const response = await api.get(`/api/stores/${storeId}/payment-settings`);
        if (response.data?.enabledMethods) {
          setEnabledPaymentMethods(response.data.enabledMethods);
        }
      } catch (error) {
        // 設定が取得できない場合はデフォルト（全て有効）
        console.log('決済設定の取得に失敗しました。デフォルト設定を使用します。');
      }
    };
    if (storeId) {
      fetchPaymentSettings();
    }
  }, [storeId]);

  // 全決済方法の定義
  const allQuickMethods = [
    { id: 'cash', name: '現金', category: 'cash', apiSupported: false },
    { id: 'credit_card_square', name: 'クレカ(Square)', category: 'credit_card', apiSupported: true },
    { id: 'square_terminal', name: 'カード(端末)', category: 'credit_card', apiSupported: true, isTerminalApi: true },
    { id: 'paypay', name: 'PayPay', category: 'qr_code', apiSupported: true },
  ];

  const allAdditionalMethods = [
    { id: 'card_manual', name: 'カード(外部端末)', category: 'credit_card', apiSupported: false },
    { id: 'emoney', name: '電子マネー', category: 'emoney', apiSupported: false },
    { id: 'transport_ic', name: '交通系IC', category: 'emoney', apiSupported: false },
    { id: 'qr_other', name: 'QR決済(その他)', category: 'qr_code', apiSupported: false },
    { id: 'gift_card', name: 'ギフトカード', category: 'gift_card', apiSupported: true },
    { id: 'member_points', name: 'ポイント利用', category: 'points', apiSupported: true },
    { id: 'convenience', name: 'コンビニ払い', category: 'convenience', apiSupported: true },
    { id: 'split_payment', name: '分割払い', category: 'split', apiSupported: true },
    { id: 'credit_account', name: '売掛/ツケ', category: 'credit', apiSupported: true },
  ];

  // 店舗設定でフィルタリング
  // 設定がない場合（null）はデフォルト3つのみ表示（現金/クレカ/PayPay）
  // 追加決済方法は設定がある場合のみ表示
  const quickMethods = enabledPaymentMethods
    ? allQuickMethods.filter(m => enabledPaymentMethods.includes(m.id))
    : allQuickMethods; // デフォルト3つ

  const additionalMethods = enabledPaymentMethods
    ? allAdditionalMethods.filter(m => enabledPaymentMethods.includes(m.id))
    : []; // 設定がない場合は追加決済を非表示

  if (!order) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="textSecondary" gutterBottom>
          会計する注文がありません
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate(`/pos/${storeId}`)}
          sx={{ mt: 2 }}
        >
          注文入力に戻る
        </Button>
      </Box>
    );
  }

  const handlePayment = async (overrides = {}) => {
    if (!selectedPaymentMethod) {
      setSnackbar({
        open: true,
        message: "決済方法を選択してください",
        severity: "warning",
      });
      return;
    }

    // overridesで渡された値を優先（ManualPaymentDialogのrace condition対策）
    const paymentReference = overrides.reference ?? reference;
    const paymentNote = overrides.note ?? note;

    try {
      const isCash = selectedPaymentMethod.id === 'cash';
      const isManualRecording = !selectedPaymentMethod.apiSupported || !selectedPaymentMethod.apiAvailable;

      // 既存注文（モバイルオーダーなど）の場合は、payment_statusを更新
      // H-1 FIX: テーブルグループの複数注文に対応
      // C-8 FIX: Promise.allSettledで部分失敗を適切に処理
      if (order.existingOrderIds && order.existingOrderIds.length > 0) {
        // 複数注文を一括更新（部分失敗を許容）
        // AUDIT-11-1 FIX: safeParseAmountで安全に変換
        const receivedInt = safeParseAmount(receivedAmount);
        const totalInt = safeParseAmount(order.total);
        const results = await Promise.allSettled(order.existingOrderIds.map(orderId =>
          api.patch(`/api/orders/${orderId}/payment-status`, {
            paymentStatus: "PAID",
            paymentMethod: selectedPaymentMethod.id,
            ...(isCash && {
              receivedAmount: receivedInt,
              change: Math.max(0, receivedInt - totalInt),
            }),
          })
        ));

        // 失敗した注文をチェック
        const failures = results.filter(r => r.status === 'rejected');
        if (failures.length > 0) {
          console.error("[PAYMENT] 一部注文の更新に失敗:", failures);
          if (failures.length === order.existingOrderIds.length) {
            // 全て失敗した場合はエラー
            throw new Error("全ての注文の更新に失敗しました");
          }
          // 部分成功の場合は警告を表示しつつ続行
          setSnackbar({
            open: true,
            message: `${failures.length}件の注文更新に失敗しましたが、他の注文は完了しました`,
            severity: "warning",
          });
        }
        setPrintDialog(true);
        return;
      } else if (order.existingOrderId) {
        // 単一注文の場合
        // AUDIT-11-1 FIX: safeParseAmountで安全に変換
        const receivedIntSingle = safeParseAmount(receivedAmount);
        const totalIntSingle = safeParseAmount(order.total);
        await api.patch(`/api/orders/${order.existingOrderId}/payment-status`, {
          paymentStatus: "PAID",
          paymentMethod: selectedPaymentMethod.id,
          ...(isCash && {
            receivedAmount: receivedIntSingle,
            change: Math.max(0, receivedIntSingle - totalIntSingle),
          }),
        });
        setPrintDialog(true);
        return;
      }

      // 新規注文の場合は従来通り売上を記録
      // AUDIT-11-1 FIX: safeParseAmountで安全に変換
      const receivedIntNew = safeParseAmount(receivedAmount);
      const totalIntNew = safeParseAmount(order.total);
      const paymentData = {
        ...order,
        paymentMethod: selectedPaymentMethod.id,
        paymentMethodName: selectedPaymentMethod.name,
        ...(isCash && {
          receivedAmount: receivedIntNew,
          change: Math.max(0, receivedIntNew - totalIntNew),
        }),
        ...(isManualRecording && paymentReference && { reference: paymentReference }),
        ...(isManualRecording && paymentNote && { note: paymentNote }),
      };

      await api.post(`/api/stores/${storeId}/sales`, paymentData);
      triggerKitchenPrint(order);
      setPrintDialog(true);
    } catch (error) {
      console.error("支払い処理に失敗しました:", error);
      setPaymentError({
        type: selectedPaymentMethod?.id || 'unknown',
        message: error.response?.data?.message || "支払い処理に失敗しました",
      });
    }
  };

  const handlePrint = async () => {
    // StarWebPRNT LAN直結印刷を試行
    const result = await starWebPRNTService.printReceipt({
      storeName: order.storeName,
      storeAddress: order.storeAddress,
      storePhone: order.storePhone,
      invoiceNumber: order.invoiceNumber,
      date: new Date().toLocaleString('ja-JP'),
      tableNumber: order.tableNumber,
      items: order.items.map(item => ({
        name: item.product?.name || item.name || '商品',
        quantity: item.quantity || 1,
        price: item.price || item.unit_price || 0,
      })),
      subtotal: order.subtotal,
      tax: order.tax,
      taxBreakdown: order.taxBreakdown,
      total: order.total,
      paymentMethod: selectedPaymentMethod?.name || '',
      receivedAmount: isCashPayment ? safeParseAmount(receivedAmount) : undefined,
      change: isCashPayment ? Math.max(0, safeParseAmount(receivedAmount) - safeParseAmount(order.total)) : undefined,
    });

    if (!result.success) {
      // StarWebPRNT失敗時はブラウザ印刷にフォールバック
      window.print();
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("ja-JP", {
      style: "currency",
      currency: "JPY",
    }).format(value);
  };

  const getTranslatedText = (translationObj, defaultLang = 'ja') => {
    if (!translationObj) return '';
    if (typeof translationObj !== 'object') return translationObj;
    return translationObj[defaultLang] || translationObj.ja || translationObj.en || translationObj.zh || '';
  };

  const isCashPayment = selectedPaymentMethod?.id === 'cash';
  const isSquarePayment = selectedPaymentMethod?.id === 'credit_card_square' && selectedPaymentMethod?.apiAvailable;
  const isSquareTerminalPayment = selectedPaymentMethod?.id === 'square_terminal';
  const isPayPayPayment = selectedPaymentMethod?.id === 'paypay' && selectedPaymentMethod?.apiAvailable;
  const isGiftCardPayment = selectedPaymentMethod?.id === 'gift_card';
  const isMemberPointPayment = selectedPaymentMethod?.id === 'member_points';
  const isConveniencePayment = selectedPaymentMethod?.id === 'convenience';
  const isSplitPayment = selectedPaymentMethod?.id === 'split_payment';
  const isCreditAccountPayment = selectedPaymentMethod?.id === 'credit_account';
  const isManualRecording = selectedPaymentMethod &&
    (!selectedPaymentMethod.apiSupported || !selectedPaymentMethod.apiAvailable) &&
    !isCashPayment &&
    !isGiftCardPayment &&
    !isMemberPointPayment &&
    !isConveniencePayment &&
    !isSplitPayment &&
    !isCreditAccountPayment;

  const canPay = () => {
    if (!selectedPaymentMethod || !order) return false;
    if (isCashPayment) {
      // AUDIT-11-1 FIX: safeParseAmountでNaN防止
      const received = safeParseAmount(receivedAmount);
      const total = safeParseAmount(order.total);
      return received > 0 && received >= total;
    }
    if (isSquarePayment) {
      return true; // Squareは別のフローで処理
    }
    if (isPayPayPayment) {
      return true; // PayPayは別のフローで処理
    }
    if (isGiftCardPayment || isMemberPointPayment || isConveniencePayment || isSplitPayment || isCreditAccountPayment) {
      return true; // 専用コンポーネントで処理
    }
    return true;
  };

  // 追加決済方法の選択ハンドラ
  const handleAdditionalMethodSelect = (method) => {
    setSelectedPaymentMethod(method);
  };

  // ギフトカード決済完了ハンドラ
  const handleGiftCardSuccess = async (result) => {
    setPaymentResult(result);
    setShowGiftCardPayment(false);

    try {
      const paymentData = {
        ...order,
        paymentMethod: 'gift_card',
        paymentMethodName: 'ギフトカード',
        giftCardCode: result.cardCode,
        giftCardAmount: result.amount,
      };

      const response = await api.post(`/api/stores/${storeId}/sales`, paymentData);
      setCompletedSaleId(response.data?.sale?._id);
      triggerKitchenPrint(order);
      setPrintDialog(true);
    } catch (error) {
      console.error("売上記録に失敗しました:", error);
      setSnackbar({
        open: true,
        message: "決済は成功しましたが、売上記録に失敗しました。",
        severity: "warning",
      });
      triggerKitchenPrint(order);
      setPrintDialog(true);
    }
  };

  // 会員ポイント決済完了ハンドラ
  const handleMemberPointSuccess = async (result) => {
    setPaymentResult(result);
    setShowMemberPointPayment(false);

    try {
      const paymentData = {
        ...order,
        paymentMethod: 'member_points',
        paymentMethodName: 'ポイント利用',
        pointsUsed: result.pointsUsed,
        pointDiscount: result.amount,
      };

      const response = await api.post(`/api/stores/${storeId}/sales`, paymentData);
      setCompletedSaleId(response.data?.sale?._id);
      triggerKitchenPrint(order);
      setPrintDialog(true);
    } catch (error) {
      console.error("売上記録に失敗しました:", error);
      setSnackbar({
        open: true,
        message: "決済は成功しましたが、売上記録に失敗しました。",
        severity: "warning",
      });
      triggerKitchenPrint(order);
      setPrintDialog(true);
    }
  };

  // コンビニ決済完了ハンドラ
  const handleConvenienceSuccess = async (result) => {
    setPaymentResult(result);
    setShowConveniencePayment(false);

    try {
      const paymentData = {
        ...order,
        paymentMethod: 'convenience',
        paymentMethodName: `コンビニ払い（${result.convenienceStoreName}）`,
        paymentCode: result.paymentCode,
        paymentDeadline: result.paymentDeadline,
      };

      const response = await api.post(`/api/stores/${storeId}/sales`, paymentData);
      setCompletedSaleId(response.data?.sale?._id);
      triggerKitchenPrint(order);
      setPrintDialog(true);
    } catch (error) {
      console.error("売上記録に失敗しました:", error);
      setSnackbar({
        open: true,
        message: "支払い番号の発行には成功しました。",
        severity: "info",
      });
      triggerKitchenPrint(order);
      setPrintDialog(true);
    }
  };

  // 分割決済完了ハンドラ
  const handleSplitPaymentSuccess = async (result) => {
    setPaymentResult(result);
    setShowSplitPayment(false);

    try {
      const paymentData = {
        ...order,
        paymentMethod: 'split_payment',
        paymentMethodName: '分割払い',
        splitPayments: result.payments,
      };

      const response = await api.post(`/api/stores/${storeId}/sales`, paymentData);
      setCompletedSaleId(response.data?.sale?._id);
      triggerKitchenPrint(order);
      setPrintDialog(true);
    } catch (error) {
      console.error("売上記録に失敗しました:", error);
      setSnackbar({
        open: true,
        message: "決済は成功しましたが、売上記録に失敗しました。",
        severity: "warning",
      });
      triggerKitchenPrint(order);
      setPrintDialog(true);
    }
  };

  // 売掛決済完了ハンドラ
  const handleCreditPaymentSuccess = async (result) => {
    setPaymentResult(result);
    setShowCreditPayment(false);

    try {
      const paymentData = {
        ...order,
        paymentMethod: 'credit_account',
        paymentMethodName: '売掛/ツケ',
        creditAccountId: result.accountId,
        customerName: result.customerName,
      };

      const response = await api.post(`/api/stores/${storeId}/sales`, paymentData);
      setCompletedSaleId(response.data?.sale?._id);
      triggerKitchenPrint(order);
      setPrintDialog(true);
    } catch (error) {
      console.error("売上記録に失敗しました:", error);
      setSnackbar({
        open: true,
        message: "売掛登録は成功しましたが、売上記録に失敗しました。",
        severity: "warning",
      });
      triggerKitchenPrint(order);
      setPrintDialog(true);
    }
  };

  // 各決済キャンセルハンドラ
  const handleGiftCardCancel = () => setShowGiftCardPayment(false);
  const handleMemberPointCancel = () => setShowMemberPointPayment(false);
  const handleConvenienceCancel = () => setShowConveniencePayment(false);
  const handleSplitPaymentCancel = () => setShowSplitPayment(false);
  const handleCreditPaymentCancel = () => setShowCreditPayment(false);

  // Square自動決済成功時のハンドラ
  const handleSquareSuccess = async (result) => {
    setPaymentResult(result);
    setShowSquarePayment(false);

    try {
      // 売上を記録
      const paymentData = {
        ...order,
        paymentMethod: 'credit_card_square',
        paymentMethodName: 'クレジットカード（Square）',
        transactionId: result.transactionId,
        cardBrand: result.cardBrand,
        last4: result.last4,
        receiptUrl: result.receiptUrl,
      };

      await api.post(`/api/stores/${storeId}/sales`, paymentData);
      triggerKitchenPrint(order);
      setPrintDialog(true);
    } catch (error) {
      console.error("売上記録に失敗しました:", error);
      setSnackbar({
        open: true,
        message: "決済は成功しましたが、売上記録に失敗しました。管理者に連絡してください。",
        severity: "warning",
      });
      triggerKitchenPrint(order);
      setPrintDialog(true);
    }
  };

  // Square決済エラー時のハンドラ
  const handleSquareError = (error) => {
    console.error("Square決済エラー:", error);
    setPaymentError({
      type: 'square',
      message: error?.message || "カード決済に失敗しました",
    });
    setShowSquarePayment(false);
  };

  // Square決済キャンセル時のハンドラ
  const handleSquareCancel = () => {
    setShowSquarePayment(false);
  };

  // PayPay自動決済成功時のハンドラ
  const handlePayPaySuccess = async (result) => {
    setPaymentResult(result);
    setShowPayPayPayment(false);

    try {
      // 売上を記録
      const paymentData = {
        ...order,
        paymentMethod: 'paypay',
        paymentMethodName: 'PayPay',
        transactionId: result.paymentId,
        merchantPaymentId: result.merchantPaymentId,
      };

      await api.post(`/api/stores/${storeId}/sales`, paymentData);
      triggerKitchenPrint(order);
      setPrintDialog(true);
    } catch (error) {
      console.error("売上記録に失敗しました:", error);
      setSnackbar({
        open: true,
        message: "決済は成功しましたが、売上記録に失敗しました。管理者に連絡してください。",
        severity: "warning",
      });
      triggerKitchenPrint(order);
      setPrintDialog(true);
    }
  };

  // PayPay決済エラー時のハンドラ
  const handlePayPayError = (error) => {
    console.error("PayPay決済エラー:", error);
    setPaymentError({
      type: 'paypay',
      message: error?.message || "PayPay決済に失敗しました",
    });
    setShowPayPayPayment(false);
  };

  // PayPay決済キャンセル時のハンドラ
  const handlePayPayCancel = () => {
    setShowPayPayPayment(false);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: "auto" }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          お会計
        </Typography>

        {/* 注文情報 */}
        {(order.tableNumber || order.id) && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
            {order.tableNumber && (
              <Typography variant="body2" color="text.secondary">
                テーブル: {order.tableNumber}{order.area ? ` (${order.area})` : ''}
              </Typography>
            )}
            {order.id && (
              <Typography variant="caption" color="text.secondary">
                注文番号: {order.id.slice(0, 8)}
              </Typography>
            )}
          </Box>
        )}

        <List>
          {order.items.map((item, index) => {
            const itemName = item.product?.name || item.name || '商品';
            const itemPrice = item.price || item.unit_price || 0;
            const itemQty = item.quantity || 1;
            return (
              <ListItem key={index}>
                <ListItemText
                  primary={getTranslatedText(itemName)}
                  secondary={`${itemQty}点 × ${formatCurrency(itemPrice)}`}
                />
                <Typography>
                  {formatCurrency(itemPrice * itemQty)}
                </Typography>
              </ListItem>
            );
          })}
        </List>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography>小計</Typography>
          </Grid>
          <Grid item xs={6} sx={{ textAlign: "right" }}>
            <Typography>{formatCurrency(order.subtotal)}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography>消費税</Typography>
          </Grid>
          <Grid item xs={6} sx={{ textAlign: "right" }}>
            <Typography>{formatCurrency(order.tax)}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="h6">合計</Typography>
          </Grid>
          <Grid item xs={6} sx={{ textAlign: "right" }}>
            <Typography variant="h6">{formatCurrency(order.total)}</Typography>
          </Grid>
          {order.tipTotal > 0 && (
            <>
              <Grid item xs={6}>
                <Typography color="primary" fontWeight="bold">投げ銭</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: "right" }}>
                <Typography color="primary" fontWeight="bold">{formatCurrency(order.tipTotal)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="h6" color="primary">総計（投げ銭込）</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: "right" }}>
                <Typography variant="h6" color="primary">{formatCurrency(order.total + order.tipTotal)}</Typography>
              </Grid>
            </>
          )}
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* AUDIT-11-7 FIX: 税額警告表示 */}
        {taxWarning && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">{taxWarning}</Typography>
          </Alert>
        )}

        {/* 決済エラー時の回復UI */}
        {paymentError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setPaymentError(null);
                    // 同じ決済方法で再試行
                    if (paymentError.type === 'square') {
                      setShowSquarePayment(true);
                    } else if (paymentError.type === 'paypay') {
                      setShowPayPayPayment(true);
                    }
                  }}
                >
                  再試行
                </Button>
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setPaymentError(null);
                    setSelectedPaymentMethod(null);
                  }}
                >
                  別の方法で支払う
                </Button>
              </Box>
            }
          >
            <Typography variant="body2" fontWeight={600}>
              決済エラー
            </Typography>
            <Typography variant="body2">
              {paymentError.message}
            </Typography>
          </Alert>
        )}

        {/* 決済方法選択 */}
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
          決済方法
        </Typography>

        {/* 決済方法ボタン */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          {quickMethods.map((method) => (
            <Button
              key={method.id}
              variant={selectedPaymentMethod?.id === method.id ? 'contained' : 'outlined'}
              onClick={() => {
                setSelectedPaymentMethod(method);
                setPaymentError(null);
              }}
              sx={{ flex: 1 }}
            >
              {method.name}
            </Button>
          ))}
        </Box>

        {/* 追加決済オプション（店舗設定で有効化されている場合のみ表示） */}
        {additionalMethods.length > 0 && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              その他の決済
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {additionalMethods.map((method) => (
                <Button
                  key={method.id}
                  variant={selectedPaymentMethod?.id === method.id ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => handleAdditionalMethodSelect(method)}
                  color={selectedPaymentMethod?.id === method.id ? 'primary' : 'inherit'}
                >
                  {method.name}
                </Button>
              ))}
            </Box>
          </>
        )}

        {/* 手動記録時の注意書き */}
        {isManualRecording && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              この決済方法は手動記録です。
              「支払い確定」を押すと決済端末への案内が表示されます。
            </Typography>
          </Alert>
        )}

        {/* 現金払い時の受取金額入力 */}
        {isCashPayment && (
          <Box sx={{ mt: 2 }}>
            {/* 金種ボタン（シンプル化: ぴったり + 主要3金種） */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant="contained"
                color="success"
                onClick={() => setReceivedAmount(String(order.total))}
                sx={{ flex: 1 }}
              >
                ぴったり
              </Button>
              {[1000, 5000, 10000].map((amount) => (
                <Button
                  key={amount}
                  variant={receivedAmount === String(amount) ? "contained" : "outlined"}
                  onClick={() => setReceivedAmount(String(amount))}
                  sx={{ flex: 1 }}
                >
                  ¥{amount.toLocaleString()}
                </Button>
              ))}
            </Box>

            <TextField
              fullWidth
              label="受取金額"
              type="number"
              value={receivedAmount}
              onChange={(e) => setReceivedAmount(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                inputProps: { min: 0 }
              }}
            />

            {/* AUDIT-11-1 FIX: 表示計算を一度だけ実行 */}
            {receivedAmount && (() => {
              const displayReceived = safeParseAmount(receivedAmount);
              const displayTotal = safeParseAmount(order.total);
              const isEnough = displayReceived >= displayTotal;
              const change = Math.max(0, displayReceived - displayTotal);
              const shortage = Math.max(0, displayTotal - displayReceived);
              return (
                <Box sx={{
                  p: 2,
                  bgcolor: isEnough ? 'success.light' : 'error.light',
                  borderRadius: 1,
                  mb: 2
                }}>
                  <Typography variant="h5" fontWeight={700} color={isEnough ? 'success.dark' : 'error.dark'}>
                    お釣り: {formatCurrency(change)}
                  </Typography>
                  {!isEnough && (
                    <Typography variant="body2" color="error.dark">
                      不足: {formatCurrency(shortage)}
                    </Typography>
                  )}
                </Box>
              );
            })()}
          </Box>
        )}

        {/* Square自動決済フォーム */}
        {isSquarePayment && showSquarePayment && (
          <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              カード情報を入力
            </Typography>
            <SquarePayment
              amount={order.total}
              orderId={order.id}
              storeId={storeId}
              onSuccess={handleSquareSuccess}
              onError={handleSquareError}
              onCancel={handleSquareCancel}
            />
          </Box>
        )}

        {/* PayPay自動決済フォーム */}
        {isPayPayPayment && showPayPayPayment && (
          <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <PayPayPayment
              amount={order.total}
              orderId={order.id}
              storeId={storeId}
              onSuccess={handlePayPaySuccess}
              onError={handlePayPayError}
              onCancel={handlePayPayCancel}
            />
          </Box>
        )}

        {/* ギフトカード決済フォーム */}
        {isGiftCardPayment && showGiftCardPayment && (
          <Box sx={{ mb: 2 }}>
            <GiftCardPayment
              orderId={order.id}
              orderTotal={order.total}
              storeId={storeId}
              onPaymentComplete={handleGiftCardSuccess}
              onCancel={handleGiftCardCancel}
            />
          </Box>
        )}

        {/* 会員ポイント決済フォーム */}
        {isMemberPointPayment && showMemberPointPayment && (
          <Box sx={{ mb: 2 }}>
            <MemberPointPayment
              orderId={order.id}
              orderTotal={order.total}
              storeId={storeId}
              onPaymentComplete={handleMemberPointSuccess}
              onCancel={handleMemberPointCancel}
              mode="use"
            />
          </Box>
        )}

        {/* コンビニ決済フォーム */}
        {isConveniencePayment && showConveniencePayment && (
          <Box sx={{ mb: 2 }}>
            <ConveniencePayment
              orderId={order.id}
              orderTotal={order.total}
              storeId={storeId}
              onPaymentComplete={handleConvenienceSuccess}
              onCancel={handleConvenienceCancel}
            />
          </Box>
        )}

        {/* 分割決済フォーム */}
        {isSplitPayment && showSplitPayment && (
          <Box sx={{ mb: 2 }}>
            <SplitPayment
              orderId={order.id}
              orderTotal={order.total}
              storeId={storeId}
              onPaymentComplete={handleSplitPaymentSuccess}
              onCancel={handleSplitPaymentCancel}
            />
          </Box>
        )}

        {/* 売掛/ツケ決済フォーム */}
        {isCreditAccountPayment && showCreditPayment && (
          <Box sx={{ mb: 2 }}>
            <CreditPayment
              orderId={order.id}
              orderTotal={order.total}
              storeId={storeId}
              onPaymentComplete={handleCreditPaymentSuccess}
              onCancel={handleCreditPaymentCancel}
            />
          </Box>
        )}

        {/* 選択中の決済方法表示 */}
        {selectedPaymentMethod &&
         !showSquarePayment && !showPayPayPayment &&
         !showGiftCardPayment && !showMemberPointPayment &&
         !showConveniencePayment && !showSplitPayment && !showCreditPayment && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.selected', borderRadius: 1 }}>
            <Typography variant="body2">
              選択中: <strong>{selectedPaymentMethod.name}</strong>
              {isManualRecording && (
                <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1 }}>
                  (手動記録)
                </Typography>
              )}
              {(isSquarePayment || isPayPayPayment || isGiftCardPayment ||
                isMemberPointPayment || isConveniencePayment || isSplitPayment || isCreditAccountPayment) && (
                <Typography component="span" variant="caption" color="success.main" sx={{ ml: 1 }}>
                  (API自動連携)
                </Typography>
              )}
            </Typography>
          </Box>
        )}

        {/* Square Terminal 決済 */}
        {isSquareTerminalPayment && showSquareTerminal && (
          <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <SquareTerminalPayment
              amount={order.total}
              orderId={order.id}
              storeId={storeId}
              onSuccess={async (result) => {
                setPaymentResult(result);
                setShowSquareTerminal(false);
                try {
                  const paymentData = {
                    ...order,
                    paymentMethod: 'square_terminal',
                    paymentMethodName: 'カード（Square Terminal）',
                    transactionId: result.checkoutId,
                    terminalPayment: true,
                  };
                  await api.post(`/api/stores/${storeId}/sales`, paymentData);
                  triggerKitchenPrint(order);
                  setPrintDialog(true);
                } catch (error) {
                  console.error("売上記録に失敗しました:", error);
                  setSnackbar({ open: true, message: "決済は成功しましたが、売上記録に失敗しました。", severity: "warning" });
                  triggerKitchenPrint(order);
                  setPrintDialog(true);
                }
              }}
              onError={(error) => {
                setPaymentError({ type: 'square_terminal', message: error?.message || "端末決済に失敗しました" });
                setShowSquareTerminal(false);
              }}
              onCancel={() => setShowSquareTerminal(false)}
            />
          </Box>
        )}

        {/* 決済ボタン */}
        {!showSquarePayment && !showPayPayPayment && !showSquareTerminal &&
         !showGiftCardPayment && !showMemberPointPayment &&
         !showConveniencePayment && !showSplitPayment && !showCreditPayment && (
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={() => {
              if (isSquarePayment) {
                setShowSquarePayment(true);
              } else if (isSquareTerminalPayment) {
                setShowSquareTerminal(true);
              } else if (isPayPayPayment) {
                setShowPayPayPayment(true);
              } else if (isGiftCardPayment) {
                setShowGiftCardPayment(true);
              } else if (isMemberPointPayment) {
                setShowMemberPointPayment(true);
              } else if (isConveniencePayment) {
                setShowConveniencePayment(true);
              } else if (isSplitPayment) {
                setShowSplitPayment(true);
              } else if (isCreditAccountPayment) {
                setShowCreditPayment(true);
              } else if (isManualRecording) {
                setShowManualPaymentDialog(true);
              } else {
                handlePayment();
              }
            }}
            disabled={!canPay()}
            sx={{ mt: 2 }}
          >
            {isSquarePayment
              ? 'カード決済に進む'
              : isSquareTerminalPayment
                ? 'Square端末で決済'
                : isPayPayPayment
                  ? 'PayPay決済に進む'
                  : isGiftCardPayment
                    ? 'ギフトカードで支払う'
                    : isMemberPointPayment
                      ? 'ポイントで支払う'
                      : isConveniencePayment
                        ? 'コンビニ払いに進む'
                        : isSplitPayment
                          ? '分割払いに進む'
                          : isCreditAccountPayment
                            ? '売掛で支払う'
                            : isManualRecording
                              ? '外部端末で決済する'
                              : '支払い確定'}
          </Button>
        )}

        {/* 手動決済確認ダイアログ */}
        <ManualPaymentDialog
          open={showManualPaymentDialog}
          onClose={() => setShowManualPaymentDialog(false)}
          onConfirm={async ({ reference: ref, note: n }) => {
            setShowManualPaymentDialog(false);
            await handlePayment({ reference: ref, note: n });
          }}
          amount={order.total}
          paymentMethodName={selectedPaymentMethod?.name || ''}
          formatCurrency={formatCurrency}
        />
      </Paper>

      <Dialog
        open={printDialog}
        onClose={() => setPrintDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>お会計完了</DialogTitle>
        <DialogContent>
          {!showDigitalReceipt ? (
            <>
              {/* 完了メッセージ */}
              <Box sx={{ textAlign: 'center', py: 2, mb: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                <Typography variant="h5" color="success.dark" fontWeight={700}>
                  ✓ 決済完了
                </Typography>
                <Typography variant="body1" color="success.dark">
                  {formatCurrency(order.total)}
                </Typography>
              </Box>

              {/* 次の注文へボタン（大きく目立つように） */}
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={() => navigate(`/pos/${storeId}`)}
                sx={{
                  py: 2,
                  mb: 3,
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  bgcolor: 'primary.main',
                  '&:hover': { bgcolor: 'primary.dark' }
                }}
              >
                次の注文へ
              </Button>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                レシート発行（任意）
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PrintIcon />}
                  onClick={handlePrint}
                  sx={{ flex: 1 }}
                >
                  印刷
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EmailIcon />}
                  onClick={() => setShowDigitalReceipt(true)}
                  sx={{ flex: 1 }}
                >
                  メール送信
                </Button>
              </Box>
            </>
          ) : (
            <DigitalReceipt
              orderId={order.id || completedSaleId}
              amount={order.total}
              storeId={storeId}
              onSuccess={() => {
                setShowDigitalReceipt(false);
                navigate(`/pos/${storeId}`);
              }}
              onClose={() => setShowDigitalReceipt(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* 印刷用スタイル */}
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            #print-receipt, #print-receipt * {
              visibility: visible;
            }
            #print-receipt {
              position: absolute;
              left: 0;
              top: 0;
              width: 80mm;
              padding: 5mm;
            }
          }
        `}
      </style>

      {/* 印刷用レシート（画面上は非表示） */}
      <Box
        id="print-receipt"
        sx={{
          display: 'none',
          '@media print': {
            display: 'block',
          },
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            領収書
          </Typography>
          <Typography variant="body2">
            {new Date().toLocaleString('ja-JP')}
          </Typography>
          {(order?.tableNumber || order?.area) && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {order?.area && `${order.area} `}
              {order?.tableNumber && `テーブル ${order.tableNumber}`}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 1, borderStyle: 'dashed' }} />

        {order?.items?.map((item, index) => {
          const itemName = item.product?.name || item.name || '商品';
          const displayName = typeof itemName === 'object' ? getTranslatedText(itemName) : itemName;
          return (
            <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">
                {displayName} x{item.quantity}
              </Typography>
              <Typography variant="body2">
                ¥{(item.price * item.quantity).toLocaleString()}
              </Typography>
            </Box>
          );
        })}

        <Divider sx={{ my: 1, borderStyle: 'dashed' }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2">小計</Typography>
          <Typography variant="body2">¥{order?.subtotal?.toLocaleString()}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2">消費税</Typography>
          <Typography variant="body2">¥{order?.tax?.toLocaleString()}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: 700 }}>合計</Typography>
          <Typography variant="body1" sx={{ fontWeight: 700 }}>¥{order?.total?.toLocaleString()}</Typography>
        </Box>

        {order?.tipTotal > 0 && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="body2">投げ銭</Typography>
              <Typography variant="body2">¥{order.tipTotal.toLocaleString()}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>総計</Typography>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>¥{(order.total + order.tipTotal).toLocaleString()}</Typography>
            </Box>
          </>
        )}

        {/* AUDIT-11-1 FIX: レシート印刷時もsafeParseAmount使用 */}
        {receivedAmount && (() => {
          const printReceived = safeParseAmount(receivedAmount);
          const printTotal = safeParseAmount(order?.total);
          const printChange = Math.max(0, printReceived - printTotal);
          return (
            <>
              <Divider sx={{ my: 1, borderStyle: 'dashed' }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">お預かり</Typography>
                <Typography variant="body2">¥{printReceived.toLocaleString()}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">お釣り</Typography>
                <Typography variant="body2">¥{printChange.toLocaleString()}</Typography>
              </Box>
            </>
          );
        })()}

        <Divider sx={{ my: 1, borderStyle: 'dashed' }} />

        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography variant="caption" display="block">
            ありがとうございました
          </Typography>
          <Typography variant="caption" display="block">
            またのご来店をお待ちしております
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Checkout;
